import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import MainHeader, { HeaderMenuItem } from './components/MainHeader';
import EpdRoutes, { EpdLinks } from 'routes/EpdRoutes';
import AuthService from 'services/AuthService';
import { useCurrentUserOrNullIfAnonymous } from 'hooks/useCurrentUser';
import HeaderCompanyInfoMain from './HeaderCompanyInfoMain';
import HeaderUser from './HeaderUser';
import HeaderNotifications from './HeaderNotifications';
import HeaderCompanyInfoDrawer from './HeaderCompanyInfoDrawer';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUserOrNullIfAnonymous();
  const { companyId } = useContext(CompanyContext);
  const location = useLocation();

  const isSignedIn = !!currentUser;
  const isImpersonated = isSignedIn && !!currentUser.impersonatedBy;
  const isAdmin = isSignedIn && AuthService.isAdmin();
  const isSystemAdmin = isSignedIn && AuthService.isSystemAdmin();
  const isLicenseeAdmin = isSignedIn && AuthService.isLicenseeAdmin();

  let itemsMainMenu: HeaderMenuItem[] = [
    {
      label: t('header.dashboard'),
      url: EpdLinks.dashboard(),
      visible: isSignedIn,
    },
    {
      label: t('header.companySelector'),
      url: EpdLinks.selectCompany(companyId || ''),
      visible: isSignedIn,
    },
    {
      label: t('header.collections'),
      url: EpdLinks.collections(),
      visible: isSignedIn,
    },
    {
      label: 'Resources',
      items: [
        {
          label: t('header.epdLibrary'),
          url: 'https://www.environdec.com/library',
          target: '_blank',
        },
        {
          label: t('header.pcrLibrary'),
          url: 'https://www.environdec.com/pcr-library',
          target: '_blank',
        },
        {
          label: t('header.contactVerifier'),
          url: EpdRoutes.resources.contactVerifier,
        },
        {
          label: t('header.pricing'),
          url: 'https://www.environdec.com/pricing',
          target: '_blank',
        },
      ],
    },
    {
      label: t('header.administration'),
      items: [
        {
          label: t('header.registers.title'),
          items: [
            {
              label: t('header.registers.children.epds'),
              url: EpdRoutes.admin.epds.search,
            },
            {
              label: t('header.registers.children.users'),
              url: EpdRoutes.admin.users.users,
            },
            {
              label: t('header.registers.children.organisations'),
              url: EpdRoutes.admin.companies.search,
            },
            {
              label: t('header.registers.children.licensees'),
              url: EpdRoutes.admin.licensees.licensees,
            },
            {
              label: t('header.registers.children.collocationCenters'),
              url: EpdRoutes.admin.collocationCenters,
            },
            {
              label: t('header.registers.children.pcrs'),
              url: EpdRoutes.admin.pcrs.pcrs,
            },
          ],
        },
        {
          label: t('header.finance.title'),
          items: [
            {
              label: t('header.finance.children.invoices'),
              url: EpdRoutes.admin.invoices.invoices,
            },
            {
              label: t('header.finance.children.fortnoxInvoice'),
              url: EpdRoutes.admin.integration.FortnoxInvoicesPage,
            },
            {
              label: t('header.finance.children.fortnox'),
              url: EpdRoutes.admin.integration.fortnoxIntegrationPage,
            },
          ],
        },
        {
          label: t('header.digitalisation.title'),
          items: [
            {
              label: t('header.digitalisation.children.tools'),
              url: EpdRoutes.admin.tools.search,
            },
            {
              label: t('header.digitalisation.children.ilcd'),
              url: EpdRoutes.admin.integration.ilcdEpd,
            },
            {
              label: t('header.digitalisation.children.dataSources'),
              url: EpdRoutes.admin.dataSources.dataSources,
            },
          ],
        },
        {
          label: t('header.reports.title'),
          items: [
            {
              label: t('header.reports.children.kpis'),
              url: EpdRoutes.admin.kpis.summary,
            },
            {
              label: t('header.reports.children.verifiers'),
              url: EpdRoutes.admin.verifiers.summary,
            },
            {
              label: t('header.reports.children.epds'),
              url: EpdRoutes.admin.epdsreport.summary,
            },
          ],
        },
        {
          label: `\u2699 ${t('header.system.title')}`,
          items: [
            {
              label: t('header.system.children.validationRules'),
              url: EpdRoutes.admin.system.validationRules,
              visible: isSystemAdmin,
            },
            {
              label: t('header.system.children.dictionaries'),
              url: EpdRoutes.admin.dictionaries.dictionaries,
              visible: isSystemAdmin,
            },
            {
              label: t('header.system.children.lcaSoftware'),
              url: EpdRoutes.admin.system.lcaSoftware,
            },
            {
              label: t('header.system.children.notifications'),
              url: EpdRoutes.admin.system.notifications,
              visible: isSystemAdmin,
            },
            {
              label: t('header.system.children.featureFlags'),
              url: EpdRoutes.admin.system.featureFlags,
              visible: isSystemAdmin,
            },
          ],
          visible: isAdmin || isSystemAdmin,
        },
      ],
      visible: isAdmin,
    },
    {
      label: t('header.administration'),
      items: [
        {
          label: t('header.registers.title'),
          items: [
            {
              label: t('header.registers.children.epds'),
              url: EpdRoutes.admin.epds.search,
            },
            {
              label: t('header.registers.children.users'),
              url: EpdRoutes.admin.users.users,
            },
            {
              label: t('header.registers.children.organisations'),
              url: EpdRoutes.admin.companies.search,
            },
          ],
        },
      ],
      visible: !isAdmin && isLicenseeAdmin,
    },
  ];
  itemsMainMenu = addCurrentPageClassToActiveLink(itemsMainMenu, location.pathname);

  return (
    <MainHeader
      model={itemsMainMenu}
      logoLinkPath={isSignedIn ? EpdLinks.dashboard() : EpdRoutes.login}
      menuEnd={
        currentUser && (
          <>
            <HeaderCompanyInfoMain />

            <HeaderUser isImpersonated={isImpersonated} />

            <HeaderNotifications />
          </>
        )
      }
      drawerStart={currentUser && <HeaderCompanyInfoDrawer />}
      isImpersonated={isImpersonated}
      isSystemAdmin={!!isSystemAdmin}
    />
  );
};

export default Header;

function addCurrentPageClassToActiveLink(links: HeaderMenuItem[], currentPathname: string): HeaderMenuItem[] {
  return links.map((link) => {
    if (hasCurrentLink(link, currentPathname)) {
      return {
        ...link,
        isCurrentPage: true,
      };
    }

    return link;
  });
}

function hasCurrentLink(menuItem: HeaderMenuItem, currentPathname: string): boolean {
  if (menuItem.url === currentPathname) {
    return true;
  }

  if (menuItem.items) {
    return menuItem.items.some((child) =>
      Array.isArray(child)
        ? child.some((grandchild) => hasCurrentLink(grandchild, currentPathname))
        : hasCurrentLink(child, currentPathname)
    );
  }

  return false;
}
